/*
 * popover-select.js
 *
 * Generic popover-select component + popover-tabs (tablist) variant.
 *
 * Design mirror of theme-picker.js — same open/close, cursor, ARIA,
 * click-outside, and Lucide hydration patterns — but generic (not
 * theme-specific). Theme metadata reading that is theme-picker-specific
 * stays in theme-picker.js.
 *
 * Public API (window.popoverSelect):
 *   setValue(triggerEl, value)  — programmatically select a value
 *   getValue(triggerEl)         — returns current string value
 *   open(triggerEl)             — open popover
 *   close(triggerEl)            — close popover
 *
 * Events fired on the root element:
 *   popover-select:change  → detail: { value, label }
 *
 * Markup contract (set by popover_select macro):
 *   [data-popover-select]         root container
 *     [data-popover-select-trigger]  the <button>
 *     [data-popover-select-panel]    the popover (role=listbox, hidden)
 *       [data-popover-select-search]  optional search <input>
 *       .ps-option[data-popover-value][role=option][aria-selected]
 *
 * Popover-tabs (tablist mode):
 *   [data-popover-tabs]           root container (no popover, inline)
 *     .pt-tab[role=tab][aria-selected][data-tab-value]
 */
(function () {
    'use strict';

    /* ═══════════════════════════════════════════════════════════
       POPOVER-SELECT
    ═══════════════════════════════════════════════════════════ */

    // Map from trigger element → controller object for programmatic API.
    const _instances = new WeakMap();

    function initSelect(root) {
        const trigger = root.querySelector('[data-popover-select-trigger]');
        const panel   = root.querySelector('[data-popover-select-panel]');
        if (!trigger || !panel) return;
        // Idempotency guard — `init()` re-runs on every `popover-select:reinit`
        // (HTMX/Turbo partial swaps). Without this, the same root gets a second
        // set of option click/mouseenter handlers + duplicate document-level
        // listeners, so a single click selects (and fires change) twice.
        if (_instances.has(trigger)) return;

        const searchInput = panel.querySelector('[data-popover-select-search]');
        const listEl      = panel.querySelector('[data-popover-select-list]') || panel;
        // Native hidden <select> for form-post compat (native=true on macro).
        const nativeSelect = root.querySelector('[data-popover-select-native]');

        // Live node list — filtered by visibility when search active.
        let allOptions = Array.from(panel.querySelectorAll('[data-popover-value]'));

        // ── Build metadata from DOM ──────────────────────────────
        // Extract label / icon / swatch from each row without JSON.
        function buildMeta(opt) {
            const labelEl   = opt.querySelector('.ps-option-label');
            const descEl    = opt.querySelector('.ps-option-desc');
            const iconEl    = opt.querySelector('.ps-option-icon [data-lucide]') ||
                              opt.querySelector('.ps-option-icon [class*="lucide-"]');
            const swatchEl  = opt.querySelector('.ps-option-swatch');
            const label     = labelEl ? labelEl.textContent.trim() : (opt.textContent.trim());
            const desc      = descEl  ? descEl.textContent.trim()  : '';
            const iconName  = iconEl  ? (iconEl.getAttribute('data-lucide') ||
                                         (iconEl.getAttribute('class') || '').match(/lucide-([\w-]+)/)?.[1] || '')
                                       : '';
            const swatch    = swatchEl ? swatchEl.style.background : '';
            return { label, desc, iconName, swatch };
        }

        // ── State ────────────────────────────────────────────────
        let open   = false;
        let cursor = 0;

        // ── Helpers ──────────────────────────────────────────────

        function visibleOptions() {
            return allOptions.filter(o => !o.closest('[hidden]') && o.offsetParent !== null);
        }

        function applyCursor(opts) {
            opts = opts || visibleOptions();
            opts.forEach((o, i) => o.classList.toggle('is-cursor', i === cursor));
            const node = opts[cursor];
            if (node && typeof node.scrollIntoView === 'function') {
                node.scrollIntoView({ block: 'nearest' });
            }
            // Update aria-activedescendant on whichever element holds focus.
            // When a search field is present it is the focused control, so the
            // active descendant must be declared on it (not the panel) for
            // screen readers to announce the highlighted option. Falls back to
            // the panel when there is no search field.
            if (node) {
                const owner = searchInput || panel;
                owner.setAttribute('aria-activedescendant', node.id || '');
            }
        }

        function currentValue() {
            const sel = allOptions.find(o => o.getAttribute('aria-selected') === 'true');
            return sel ? sel.dataset.popoverValue : '';
        }

        function updateTriggerDisplay(value) {
            const opt = allOptions.find(o => o.dataset.popoverValue === value);
            if (!opt) return;

            const { label, iconName, swatch } = buildMeta(opt);

            // Label text
            const labelSpan = trigger.querySelector('.ps-trigger-label');
            if (labelSpan) labelSpan.textContent = label;

            // Icon — swap data-lucide if name changed (mirrors theme-picker pattern)
            const triggerIconWrap = trigger.querySelector('.ps-trigger-icon');
            if (triggerIconWrap && iconName) {
                const existing = triggerIconWrap.querySelector('[data-lucide],[class*="lucide-"]');
                if (existing) {
                    const cls = existing.getAttribute('class') || '';
                    const currentName = existing.getAttribute('data-lucide') ||
                                        (cls.match(/lucide-([\w-]+)/) || [])[1];
                    if (currentName !== iconName) {
                        const fresh = document.createElement('i');
                        fresh.setAttribute('data-lucide', iconName);
                        fresh.setAttribute('aria-hidden', 'true');
                        fresh.className = 'inline-block h-4 w-4';
                        existing.replaceWith(fresh);
                        _hydrateLucide();
                    }
                }
            }

            // Swatch colour
            const triggerSwatch = trigger.querySelector('.ps-trigger-swatch');
            if (triggerSwatch && swatch) {
                triggerSwatch.style.background = swatch;
            }
        }

        function syncSelected(value) {
            allOptions.forEach(o => {
                const match = o.dataset.popoverValue === value;
                o.setAttribute('aria-selected', match ? 'true' : 'false');
                o.classList.toggle('is-active', match);
            });
            if (nativeSelect) nativeSelect.value = value;
            updateTriggerDisplay(value);
        }

        // ── Lucide hydration (safe, no double-init) ─────────────
        function _hydrateLucide() {
            if (window.Lucide) {
                window.Lucide.createIcons({ icons: window.Lucide.icons });
            } else if (window.lucide && typeof window.lucide.createIcons === 'function') {
                window.lucide.createIcons();
            }
        }

        // ── Fixed-position anchoring ─────────────────────────────
        // The panel is a DOM child of the trigger's wrapper, which often
        // lives inside a card/row with overflow:hidden (rounded corners) or
        // overflow:auto (scroll area). An absolutely-positioned panel is
        // sliced off by that clip. Re-anchor the open panel with
        // position:fixed in viewport coordinates so it escapes the clip —
        // same approach as anchored-menu.js for the "⋮" action menus.
        var _GAP = 4, _EDGE = 8;
        function anchorPanel() {
            var r = trigger.getBoundingClientRect();
            // Ensure the panel is at least as wide as the trigger.
            if (!panel.style.minWidth) panel.style.minWidth = r.width + 'px';
            var pw = panel.offsetWidth || r.width;
            var ph = panel.offsetHeight || 0;
            var left = r.left;
            var top  = r.bottom + _GAP;
            // Clamp horizontally inside the viewport.
            if (left + pw > window.innerWidth - _EDGE) {
                left = window.innerWidth - _EDGE - pw;
            }
            if (left < _EDGE) left = _EDGE;
            // Flip above the trigger when it would overflow the bottom and
            // there is room above (the exact case in the screenshot: a select
            // near a card's lower edge).
            if (top + ph > window.innerHeight - _EDGE) {
                var up = r.top - ph - _GAP;
                if (up >= _EDGE) top = up;
            }
            panel.style.position = 'fixed';
            panel.style.top    = top + 'px';
            panel.style.left   = left + 'px';
            panel.style.right  = 'auto';
            panel.style.bottom = 'auto';
            panel.style.margin = '0';
            // Once fixed, the panel must paint above the app chrome (topbar
            // z=900, sidebar z=1000) even when flipped upward near the top of
            // the viewport. The CSS z-index (var(--z-dropdown)) can be capped
            // by a positioned ancestor's stacking context, so max it out here —
            // same belt-and-suspenders escape as anchored-menu.js.
            panel.style.zIndex = '2147483000';
        }
        function clearAnchor() {
            panel.style.position = '';
            panel.style.top = panel.style.left = '';
            panel.style.right = panel.style.bottom = '';
            panel.style.margin = '';
            panel.style.zIndex = '';
        }
        function _reanchor() { if (open) anchorPanel(); }

        // ── Open / close ─────────────────────────────────────────
        function setOpen(next) {
            open = next;
            panel.hidden = !open;
            trigger.setAttribute('aria-expanded', open ? 'true' : 'false');

            if (open) {
                // Anchor now that the panel is measurable (hidden=false above).
                anchorPanel();
                window.addEventListener('scroll', _reanchor, true);
                window.addEventListener('resize', _reanchor);
            } else {
                window.removeEventListener('scroll', _reanchor, true);
                window.removeEventListener('resize', _reanchor);
                clearAnchor();
            }

            if (open) {
                // Reset search
                if (searchInput) {
                    searchInput.value = '';
                    filterOptions('');
                }
                // Cursor starts on selected row
                const opts = visibleOptions();
                const sel  = currentValue();
                const idx  = opts.findIndex(o => o.dataset.popoverValue === sel);
                cursor = Math.max(idx, 0);
                applyCursor(opts);
                // Focus: search input if searchable, else panel
                if (searchInput) {
                    searchInput.focus();
                } else {
                    panel.focus();
                }
            }
        }

        // ── Search / filter ──────────────────────────────────────
        let _searchTimer;

        function filterOptions(query) {
            const q = query.toLowerCase().trim();
            let hasVisible = false;
            allOptions.forEach(opt => {
                const { label, desc } = buildMeta(opt);
                const match = !q || label.toLowerCase().includes(q) || desc.toLowerCase().includes(q);
                // Hide/show: wrap each option in its own container?
                // Options sit directly in list; toggle display via class.
                opt.style.display = match ? '' : 'none';
                if (match) hasVisible = true;
            });
            // Empty state
            let emptyEl = panel.querySelector('.ps-empty');
            if (!hasVisible) {
                if (!emptyEl) {
                    emptyEl = document.createElement('div');
                    emptyEl.className = 'ps-empty';
                    emptyEl.textContent = 'No results';
                    listEl.appendChild(emptyEl);
                }
                emptyEl.hidden = false;
            } else if (emptyEl) {
                emptyEl.hidden = true;
            }
            // Reset cursor to first visible
            cursor = 0;
            applyCursor(visibleOptions());
        }

        if (searchInput) {
            searchInput.addEventListener('input', () => {
                clearTimeout(_searchTimer);
                _searchTimer = setTimeout(() => filterOptions(searchInput.value), 80);
            });
            // Arrow keys from search field navigate list
            searchInput.addEventListener('keydown', handleKeydown);
        }

        // ── Keyboard navigation ──────────────────────────────────
        function handleKeydown(e) {
            if (!open) return;
            const opts = visibleOptions();
            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    cursor = Math.min(cursor + 1, opts.length - 1);
                    applyCursor(opts);
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    cursor = Math.max(cursor - 1, 0);
                    applyCursor(opts);
                    break;
                case 'Home':
                    e.preventDefault();
                    cursor = 0;
                    applyCursor(opts);
                    break;
                case 'End':
                    e.preventDefault();
                    cursor = Math.max(opts.length - 1, 0);
                    applyCursor(opts);
                    break;
                case 'Enter':
                    e.preventDefault();
                    if (opts[cursor]) selectOption(opts[cursor]);
                    break;
                case 'Escape':
                    e.preventDefault();
                    setOpen(false);
                    trigger.focus();
                    break;
                case 'Tab':
                    setOpen(false);
                    break;
            }
        }

        panel.addEventListener('keydown', handleKeydown);

        // ── Mouse events ─────────────────────────────────────────
        allOptions.forEach((opt, i) => {
            opt.addEventListener('mouseenter', () => {
                cursor = visibleOptions().indexOf(opt);
                if (cursor < 0) cursor = 0;
                applyCursor();
            });
            opt.addEventListener('click', () => selectOption(opt));
        });

        // ── Trigger click ────────────────────────────────────────
        trigger.addEventListener('click', (e) => {
            e.stopPropagation();
            setOpen(!open);
        });

        // ── Click-outside ────────────────────────────────────────
        document.addEventListener('mousedown', (e) => {
            if (!open) return;
            if (!root.contains(e.target)) setOpen(false);
        });

        // ── Escape from document level ───────────────────────────
        document.addEventListener('keydown', (e) => {
            if (!open) return;
            if (e.key === 'Escape') { setOpen(false); trigger.focus(); }
        });

        // ── Select ───────────────────────────────────────────────
        function selectOption(opt) {
            if (opt.getAttribute('aria-disabled') === 'true') return;
            const value = opt.dataset.popoverValue;
            syncSelected(value);
            setOpen(false);
            trigger.focus();
            const { label } = buildMeta(opt);

            // Fire native custom event (works with Alpine @event listeners too)
            const ev = new CustomEvent('popover-select:change', {
                bubbles: true,
                detail: { value, label }
            });
            root.dispatchEvent(ev);

            // Execute inline on_change expression if set via data attribute.
            // Provides a framework-agnostic alternative to @event listeners.
            const expr = root.dataset.popoverOnChange;
            if (expr) {
                try {
                    // eslint-disable-next-line no-new-func
                    (new Function('value', 'label', 'event', expr))(value, label, ev);
                } catch (e) {
                    console.warn('[popover-select] on_change expression error:', e);
                }
            }
        }

        // ── Programmatic API ─────────────────────────────────────
        const ctrl = {
            getValue:  () => currentValue(),
            setValue:  (value) => { syncSelected(value); },
            open:      () => setOpen(true),
            close:     () => setOpen(false),
        };
        _instances.set(trigger, ctrl);

        // Initialise display from current selection
        const initVal = currentValue();
        if (initVal) updateTriggerDisplay(initVal);
    }

    /* ═══════════════════════════════════════════════════════════
       POPOVER-TABS  (inline tablist — no popover panel)
    ═══════════════════════════════════════════════════════════ */

    function initTabs(root) {
        const tabs = Array.from(root.querySelectorAll('[role="tab"]'));
        if (!tabs.length) return;
        // Idempotency guard — `init()` re-runs on `popover-select:reinit`.
        // Re-binding the same tablist stacks keydown/click handlers, double-
        // firing popover-tabs:change. Mark the root once it's wired.
        if (root.dataset.ptInit === '1') return;
        root.dataset.ptInit = '1';

        let cursor = tabs.findIndex(t => t.getAttribute('aria-selected') === 'true' || t.classList.contains('active'));
        if (cursor < 0) cursor = 0;

        function moveCursor(idx) {
            if (idx < 0 || idx >= tabs.length) return;
            cursor = idx;
            // Focus the tab — for pure-link tabs this is fine.
            tabs[cursor].focus();
        }

        function selectTab(tab) {
            tabs.forEach(t => {
                const selected = t === tab;
                t.setAttribute('aria-selected', selected ? 'true' : 'false');
                t.classList.toggle('active', selected);
                // tabindex roving pattern
                t.setAttribute('tabindex', selected ? '0' : '-1');
            });
            cursor = tabs.indexOf(tab);
            root.dispatchEvent(new CustomEvent('popover-tabs:change', {
                bubbles: true,
                detail: { value: tab.dataset.tabValue || tab.href || tab.textContent.trim() }
            }));
        }

        // Initialise tabindex on all tabs
        tabs.forEach((t, i) => {
            t.setAttribute('tabindex', i === cursor ? '0' : '-1');
        });

        root.addEventListener('keydown', (e) => {
            switch (e.key) {
                case 'ArrowRight':
                    e.preventDefault();
                    moveCursor((cursor + 1) % tabs.length);
                    break;
                case 'ArrowLeft':
                    e.preventDefault();
                    moveCursor((cursor - 1 + tabs.length) % tabs.length);
                    break;
                case 'Home':
                    e.preventDefault();
                    moveCursor(0);
                    break;
                case 'End':
                    e.preventDefault();
                    moveCursor(tabs.length - 1);
                    break;
                case 'Enter':
                case ' ':
                    // For <a> tabs Enter already fires click; for <button> tabs
                    // we fire selectTab and let any existing onclick handle nav.
                    if (tabs[cursor].tagName === 'BUTTON') {
                        e.preventDefault();
                        tabs[cursor].click();
                    }
                    break;
            }
        });

        // Click → update aria-selected for button tabs (link tabs navigate away)
        tabs.forEach(t => {
            if (t.tagName === 'BUTTON') {
                t.addEventListener('click', () => selectTab(t));
            } else {
                // <a> tabs: just move cursor visually on click so keyboard
                // focus lands correctly if the user stays on the page.
                t.addEventListener('click', () => { cursor = tabs.indexOf(t); });
            }
        });
    }

    /* ═══════════════════════════════════════════════════════════
       PUBLIC API
    ═══════════════════════════════════════════════════════════ */

    window.popoverSelect = {
        /**
         * Programmatically set the value of a popover-select instance.
         * @param {HTMLElement} triggerEl   The [data-popover-select-trigger] button.
         * @param {string}      value       Value matching a data-popover-value row.
         */
        setValue(triggerEl, value) {
            const ctrl = _instances.get(triggerEl);
            if (ctrl) ctrl.setValue(value);
        },
        /**
         * Get the current value of a popover-select instance.
         * @param {HTMLElement} triggerEl
         * @returns {string}
         */
        getValue(triggerEl) {
            const ctrl = _instances.get(triggerEl);
            return ctrl ? ctrl.getValue() : '';
        },
        /** Open the popover for a given trigger element. */
        open(triggerEl) {
            const ctrl = _instances.get(triggerEl);
            if (ctrl) ctrl.open();
        },
        /** Close the popover for a given trigger element. */
        close(triggerEl) {
            const ctrl = _instances.get(triggerEl);
            if (ctrl) ctrl.close();
        },
    };

    /* ═══════════════════════════════════════════════════════════
       BOOT
    ═══════════════════════════════════════════════════════════ */

    function init() {
        document.querySelectorAll('[data-popover-select]').forEach(initSelect);
        document.querySelectorAll('[data-popover-tabs]').forEach(initTabs);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    // Re-run on HTMX / Turbo partial swaps if used
    document.addEventListener('popover-select:reinit', init);
})();
